import {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {zodResolver} from "@hookform/resolvers/zod";
import {SubmitHandler, useForm} from "react-hook-form";
import {z} from "zod";
import {
    Form,
    FormControl, FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {Button} from "@/components/ui/button";
import {useToast} from "@/components/ui/use-toast";
import {getMentorById, updateMentorWithDetails} from "@/services/mentorService.ts";
import {Mentor, UpdateMentorDetailsDto} from "@/types/Mentor.ts";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {queryKeys} from "@/services/queryKeyFactory.ts";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card.tsx";
import {Skeleton} from "@/components/ui/skeleton.tsx";
import {Checkbox} from "@/components/ui/checkbox.tsx";
import {RadioGroup, RadioGroupItem} from "@/components/ui/radio-group"
import {getItemsOfAllTypes} from "@/services/itemService.ts";
import {Loader2} from "lucide-react";
import {useTranslation} from "react-i18next";
import {getLocalizedFieldName} from "@/utils/translation-helper.ts";
import {Criteria} from "@/types/Criteria.ts";
import {getAffinityGroups} from "@/services/affinityGroupService.ts";
import {useAuth} from "@/contexts/AuthContext.tsx";
import {UserRole} from "@/types/UserRole.ts";

const MentorProfileUpdateDetails = () => {
    const {t: vt} = useTranslation("validation");
    const {t} = useTranslation();
    const {id} = useParams();
    const {loggedInUser} = useAuth();
    const isAdmin = loggedInUser?.role === UserRole.ADMIN;
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const {toast} = useToast();
    const [isMentorFetched, setIsMentorFetched] = useState(false);

    const {data: mentor} = useQuery({
        queryKey: queryKeys.fetchMentors.one,
        queryFn: () => getMentorById(id ? parseInt(id) : -1),
    });

    const {data: criteria} = useQuery({
        queryKey: queryKeys.fetchCriteria.all,
        queryFn: () => getItemsOfAllTypes(),
    });

    const {data: affinityGroups = []} = useQuery({
        queryKey: ["affinityGroups"],
        queryFn: () => getAffinityGroups(),
    });

    const detailsFormSchema = z.object({
        languages: z.array(z.number()).nonempty({
            message: vt("selectAtLeastOne", {fieldName: t("criteria.language")}),
        }),
        careerFocusId: z.number({
            required_error: vt("selectOne", {fieldName: t("criteria.careerFocus")}),
        }),
        experienceId: z.number({
            required_error: vt("selectOne", {fieldName: t("criteria.experience")}),
        }),
        affinityGroupId: z.number().nullable().optional(),
        disciplines: z.array(z.number()).nonempty({
            message: vt("selectAtLeastOne", {fieldName: t("criteria.discipline")}),
        }),
        researchExpertises: z.array(z.number()).nonempty({
            message: vt("selectAtLeastOne", {fieldName: t("criteria.researchExpertise")}),
        }),
        hasSocioeconomicDisadvantage: z.boolean({
            required_error: vt("chooseYesNo"),
        }),
        socioeconomicDisadvantages: z.array(z.number()).optional(),
        isRacialized: z.boolean({
            required_error: vt("chooseYesNo"),
        }),
        races: z.array(z.number()).optional(),
        isIndigenous: z.boolean({
            required_error: vt("chooseYesNo"),
        }),
        indigeneities: z.array(z.number()).optional(),
        hasDisability: z.boolean({
            required_error: vt("chooseYesNo"),
        }),
        disabilities: z.array(z.number()).optional(),
        isQueer: z.boolean({
            required_error: vt("chooseYesNo"),
        }),
        isMale: z.boolean({
            required_error: vt("chooseYesNo"),
        }),
        isFemale: z.boolean({
            required_error: vt("chooseYesNo"),
        }),
        sexualOrientationId: z.number().optional(),
        genderId: z.number().optional(),
    }).superRefine((data, ctx) => {
        if (data.hasSocioeconomicDisadvantage) {
            if (!data.socioeconomicDisadvantages || data.socioeconomicDisadvantages.length === 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: vt("selectOne", {fieldName: t("criteria.socioeconomicDisadvantages")}),
                    path: ['socioeconomicDisadvantages']
                });
            }
        }

        if (data.isRacialized) {
            if (!data.races || data.races.length === 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: vt("selectOne", {fieldName: t("criteria.races")}),
                    path: ['races']
                });
            }
        }

        if (data.isIndigenous) {
            if (!data.indigeneities || data.indigeneities.length === 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: vt("selectOne", {fieldName: t("criteria.indigeneities")}),
                    path: ['indigeneities']
                });
            }
        }

        if (data.hasDisability) {
            if (!data.disabilities || data.disabilities.length === 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: vt("selectOne", {fieldName: t("criteria.disabilities")}),
                    path: ['disabilities']
                });
            }
        }

        if (data.isQueer) {
            if (!data.genderId) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: vt("selectOne", {fieldName: t("criteria.gender")}),
                    path: ['genderId']
                });
            }

            if (!data.sexualOrientationId) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: vt("selectOne", {fieldName: t("criteria.sexualOrientation")}),
                    path: ['sexualOrientationId']
                });
            }
        }
    });

    type ProfileFormValues = z.infer<typeof detailsFormSchema>;

    const form = useForm<ProfileFormValues>({
        resolver: zodResolver(detailsFormSchema),
        mode: "onChange",
    });

    useEffect(() => {
        if (mentor) {
            form.reset({
                languages: mentor.languages.map(language => language.id),
                careerFocusId: mentor.careerFocusId ?? undefined,
                experienceId: mentor.experienceId ?? undefined,
                affinityGroupId: mentor.affinityGroupId ?? null,
                disciplines: mentor.disciplines.map(discipline => discipline.id),
                researchExpertises: mentor.researchExpertises.length === 0 ? [-1] : mentor.researchExpertises.map(researchExpertise => researchExpertise.id),
                hasSocioeconomicDisadvantage: mentor.hasSocioeconomicDisadvantage ?? undefined,
                socioeconomicDisadvantages: mentor.socioeconomicDisadvantages.map(socioeconomicDisadvantage => socioeconomicDisadvantage.id),
                isRacialized: mentor.isRacialized ?? undefined,
                races: mentor.races.map(race => race.id),
                isIndigenous: mentor.isIndigenous ?? undefined,
                indigeneities: mentor.indigeneities.map(indigeneity => indigeneity.id),
                hasDisability: mentor.hasDisability ?? undefined,
                disabilities: mentor.disabilities.map(disability => disability.id),
                isQueer: mentor.isQueer ?? undefined,
                genderId: mentor.genderId ?? undefined,
                sexualOrientationId: mentor.sexualOrientationId ?? undefined,
                isMale: mentor.isMale ?? undefined,
                isFemale: mentor.isFemale ?? undefined,
            });
            setIsMentorFetched(true);
        }
    }, [mentor]);

    const onRequestError = () => {
        toast({
            variant: 'destructive',
            title: t("common.errorTitle"),
            description: t("common.errorDesc"),
        });
    };

    const onUpdateSuccess = async (updatedMentor: Mentor) => {
        navigate(`/mentors/${updatedMentor.id}`);
        await queryClient.setQueryData(queryKeys.fetchMentors.one, () => {
            return updatedMentor;
        });
        toast({
            description: t("profile.updateSuccess"),
        });
    };

    const updateMutation = useMutation({
        mutationFn: ({id, ...dto}: UpdateMentorDetailsDto & {
            id: number
        }) => updateMentorWithDetails(id, dto),
        onSuccess: onUpdateSuccess,
        onError: onRequestError,
    });

    const onSubmit: SubmitHandler<z.infer<typeof detailsFormSchema>> = (values: ProfileFormValues) => {
        if (mentor && criteria) {
            const updateDto: UpdateMentorDetailsDto = {
                languages: criteria.Language.filter(language => values.languages.includes(language.id)),
                careerFocusId: values.careerFocusId,
                experienceId: values.experienceId,
                affinityGroupId: values.affinityGroupId ?? mentor.affinityGroupId ?? null,
                disciplines: criteria.Discipline.filter(discipline => values.disciplines.includes(discipline.id)),
                researchExpertises: criteria.ResearchExpertise.filter(researchExpertise => values.researchExpertises.includes(researchExpertise.id)),
                hasSocioeconomicDisadvantage: values.hasSocioeconomicDisadvantage,
                socioeconomicDisadvantages: !values.hasSocioeconomicDisadvantage ? [] : values.socioeconomicDisadvantages ?
                    criteria.SocioeconomicDisadvantage.filter(socioeconomicDisadvantage => (values.socioeconomicDisadvantages as number[]).includes(socioeconomicDisadvantage.id)) : [],
                isRacialized: values.isRacialized,
                races: !values.isRacialized ? [] : values.races ? criteria.Race.filter(race => (values.races as number[]).includes(race.id)) : [],
                isIndigenous: values.isIndigenous,
                indigeneities: !values.isIndigenous ? [] : values.indigeneities ? criteria.Indigeneity.filter(indigeneity => (values.indigeneities as number[]).includes(indigeneity.id)) : [],
                hasDisability: values.hasDisability,
                disabilities: !values.hasDisability ? [] : values.disabilities ? criteria.Disability.filter(disability => (values.disabilities as number[]).includes(disability.id)) : [],
                isQueer: values.isQueer,
                genderId: values.genderId ?? null,
                sexualOrientationId: values.sexualOrientationId ?? null,
                isMale: values.isMale,
                isFemale: values.isFemale
            };

            updateMutation.mutate({...updateDto, id: mentor.id});
        }
    }

    const YesNoRadioGroup = ({field}: any) => {
        return (
            <FormControl>
                <RadioGroup
                    onValueChange={value => field.onChange(value === 'true')}
                    value={(field.value || field.value === false) ? field.value.toString() : undefined}
                    defaultValue={(field.value || field.value === false) ? field.value.toString() : undefined}
                    className="flex flex-col space-y-1"
                >
                    <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                            <RadioGroupItem value="true"/>
                        </FormControl>
                        <FormLabel className="font-normal">
                            {t("common.yes")}
                        </FormLabel>
                    </FormItem>
                    <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                            <RadioGroupItem value="false"/>
                        </FormControl>
                        <FormLabel className="font-normal">
                            {t("common.no")}
                        </FormLabel>
                    </FormItem>
                </RadioGroup>
            </FormControl>
        );
    };

    return (
        <Card className="border border-border bg-bg-card shadow-sm">
            <CardHeader className="border-b border-border">
                <CardTitle className="text-xl font-semibold text-text-1">{t("common.details")}</CardTitle>
                <CardDescription className="text-text-2">
                    {t("mentor.detailsDesc")}
                </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
                {!isMentorFetched && (
                    <div className="flex flex-col space-y-3">
                        <div className="space-y-2">
                            <Skeleton className="h-8 w-full"/>
                            <Skeleton className="h-8 w-full"/>
                            <Skeleton className="h-8 w-full"/>
                            <Skeleton className="h-8 w-full"/>
                            <Skeleton className="h-8 w-full"/>
                            <Skeleton className="h-8 w-full"/>
                        </div>
                    </div>
                )}

                {isMentorFetched && (
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                        <FormField
                            control={form.control}
                            name="languages"
                            render={() => (
                                <FormItem>
                                    <div className="mb-4">
                                        <FormLabel className="text-base">1. {t("criteria.language")}</FormLabel>
                                        <FormDescription>
                                            {t("mentor.languageDesc")}
                                        </FormDescription>
                                    </div>
                                    {criteria?.Language.map((language) => <FormField
                                        key={language.id}
                                        control={form.control}
                                        name="languages"
                                        render={({field}) => {
                                            return <FormItem
                                                key={language.id}
                                                className="flex flex-row items-start space-x-3 space-y-0"
                                            >
                                                <FormControl>
                                                    <Checkbox
                                                        checked={field.value?.includes(language.id)}
                                                        onCheckedChange={(checked: boolean) => {
                                                            return checked
                                                                ? field.onChange([...field.value, language.id])
                                                                : field.onChange(
                                                                    field.value?.filter(
                                                                        (value) => value !== language.id,
                                                                    ),
                                                                );
                                                        }}
                                                    />
                                                </FormControl>
                                                <FormLabel className="font-normal">
                                                    {language[getLocalizedFieldName("name") as keyof Criteria]}
                                                </FormLabel>
                                            </FormItem>;
                                        }}
                                    />)}
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="careerFocusId"
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel className="text-base">2. {t("criteria.careerFocus")}</FormLabel>
                                    <Select onValueChange={value => field.onChange(parseInt(value))}
                                            value={field.value ? field.value.toString() : undefined}
                                    >
                                        <FormControl>
                                            <SelectTrigger>
                                                <SelectValue placeholder={t("mentor.selectCareerFocus")}/>
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {criteria?.CareerFocus.map((careerFocus) => (
                                                <SelectItem key={careerFocus.id} value={careerFocus.id.toString()}>
                                                    {careerFocus[getLocalizedFieldName("name") as keyof Criteria]}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormDescription>

                                    </FormDescription>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="experienceId"
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel className="text-base">3. {t("criteria.experience")}</FormLabel>
                                    <Select onValueChange={value => field.onChange(parseInt(value))}
                                            value={field.value ? field.value.toString() : undefined}
                                    >
                                        <FormControl>
                                            <SelectTrigger>
                                                <SelectValue placeholder={t("mentor.selectExperience")}/>
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {criteria?.Experience.map((experience) => (
                                                <SelectItem key={experience.id} value={experience.id.toString()}>
                                                    {experience[getLocalizedFieldName("name") as keyof Criteria]}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormDescription>

                                    </FormDescription>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="disciplines"
                            render={() => (
                                <FormItem>
                                    <div className="mb-4">
                                        <FormLabel className="text-base">4. {t("criteria.discipline")}</FormLabel>
                                        <FormDescription>
                                            {t("mentor.disciplineDesc")}
                                        </FormDescription>
                                    </div>
                                    {criteria?.Discipline.map((discipline) => (
                                        <FormField
                                            key={discipline.id}
                                            control={form.control}
                                            name="disciplines"
                                            render={({field}) => {
                                                return (
                                                    <FormItem
                                                        key={discipline.id}
                                                        className="flex flex-row items-start space-x-3 space-y-0"
                                                    >
                                                        <FormControl>
                                                            <Checkbox
                                                                checked={field.value?.includes(discipline.id)}
                                                                onCheckedChange={(checked: boolean) => {
                                                                    return checked
                                                                        ? field.onChange([...field.value, discipline.id])
                                                                        : field.onChange(
                                                                            field.value?.filter(
                                                                                (value) => value !== discipline.id,
                                                                            ),
                                                                        );
                                                                }}
                                                            />
                                                        </FormControl>
                                                        <FormLabel className="font-normal">
                                                            {discipline[getLocalizedFieldName("name") as keyof Criteria]}
                                                        </FormLabel>
                                                    </FormItem>
                                                );
                                            }}
                                        />
                                    ))}
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="researchExpertises"
                            render={() => (
                                <FormItem>
                                    <div className="mb-4">
                                        <FormLabel className="text-base">5. {t("criteria.researchExpertise")}</FormLabel>
                                        <FormDescription>
                                            {t("mentor.researchExpertiseDesc")}
                                        </FormDescription>
                                    </div>
                                    {criteria?.ResearchExpertise.map((researchExpertise) => (
                                        <FormField
                                            key={researchExpertise.id}
                                            control={form.control}
                                            name="researchExpertises"
                                            render={({field}) => {
                                                return (
                                                    <FormItem
                                                        key={researchExpertise.id}
                                                        className="flex flex-row items-start space-x-3 space-y-0"
                                                    >
                                                        <FormControl>
                                                            <Checkbox
                                                                checked={field.value?.includes(researchExpertise.id)}
                                                                onCheckedChange={(checked: boolean) => {
                                                                    return checked
                                                                        ? field.onChange([
                                                                            ...(field.value ?? []).filter(id => id !== -1),
                                                                            researchExpertise.id
                                                                        ])
                                                                        : field.onChange(
                                                                            field.value?.filter(
                                                                                (value) => value !== researchExpertise.id,
                                                                            ),
                                                                        );
                                                                }}
                                                            />
                                                        </FormControl>
                                                        <FormLabel className="font-normal">
                                                            {researchExpertise[getLocalizedFieldName("name") as keyof Criteria]}
                                                        </FormLabel>
                                                    </FormItem>
                                                );
                                            }}
                                        />
                                    ))}
                                    <FormField
                                        control={form.control}
                                        name="researchExpertises"
                                        render={({field}) => (
                                            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                                <FormControl>
                                                    <Checkbox
                                                        checked={field.value?.includes(-1)}
                                                        onCheckedChange={(checked: boolean) => {
                                                            if (checked) {
                                                                // If "None of the above" is checked, clear all other checkboxes
                                                                field.onChange([-1]);
                                                            } else {
                                                                // If "None of the above" is unchecked, remove it from the selection
                                                                field.onChange(field.value?.filter(id => id !== -1));
                                                            }
                                                        }}
                                                    />
                                                </FormControl>
                                                <FormLabel className="font-normal">
                                                    {t("common.noneOfTheAbove")}
                                                </FormLabel>
                                            </FormItem>
                                        )}
                                    />
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="hasSocioeconomicDisadvantage"
                            render={({field}) => (
                                <FormItem className="space-y-3">
                                    <div>
                                        <FormLabel className="text-base">6. {t("mentor.hasSocioeconomicDisadvantage")}</FormLabel>
                                        <FormDescription>{t("mentor.socioeconomicDisadvantageExamples")}</FormDescription>
                                    </div>
                                    <YesNoRadioGroup field={field}/>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />

                        {form.watch('hasSocioeconomicDisadvantage') && (
                            <FormField
                                control={form.control}
                                name="socioeconomicDisadvantages"
                                render={() => (
                                    <FormItem>
                                        <div className="mb-4">
                                            <FormLabel
                                                className="text-base">6a. {t("criteria.socioeconomicDisadvantages")}</FormLabel>
                                            <FormDescription>

                                            </FormDescription>
                                        </div>
                                        {criteria?.SocioeconomicDisadvantage.map((socioeconomicDisadvantage) => (
                                            <FormField
                                                key={socioeconomicDisadvantage.id}
                                                control={form.control}
                                                name="socioeconomicDisadvantages"
                                                render={({field}) => {
                                                    return (
                                                        <FormItem
                                                            key={socioeconomicDisadvantage.id}
                                                            className="flex flex-row items-start space-x-3 space-y-0"
                                                        >
                                                            <FormControl>
                                                                <Checkbox
                                                                    checked={field.value?.includes(socioeconomicDisadvantage.id)}
                                                                    onCheckedChange={(checked: boolean) => {
                                                                        return checked
                                                                            ? field.onChange([...(field.value ?? []), socioeconomicDisadvantage.id])
                                                                            : field.onChange(
                                                                                field.value?.filter(
                                                                                    (value) => value !== socioeconomicDisadvantage.id,
                                                                                ),
                                                                            );
                                                                    }}
                                                                />
                                                            </FormControl>
                                                            <FormLabel className="font-normal">
                                                                {socioeconomicDisadvantage[getLocalizedFieldName("name") as keyof Criteria]}
                                                            </FormLabel>
                                                        </FormItem>
                                                    );
                                                }}
                                            />
                                        ))}
                                        <FormMessage/>
                                    </FormItem>
                                )}
                            />
                        )}

                        <FormField
                            control={form.control}
                            name="isRacialized"
                            render={({field}) => (
                                <FormItem className="space-y-3">
                                    <FormLabel className="text-base">7. {t("mentor.isRacialized")}</FormLabel>
                                    <YesNoRadioGroup field={field}/>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />

                        {form.watch('isRacialized') && (
                            <FormField
                                control={form.control}
                                name="races"
                                render={() => (
                                    <FormItem>
                                        <div className="mb-4">
                                            <FormLabel className="text-base">7a. {t("criteria.races")}</FormLabel>
                                            <FormDescription>
                                            </FormDescription>
                                        </div>
                                        {criteria?.Race.map((race) => (
                                            <FormField
                                                key={race.id}
                                                control={form.control}
                                                name="races"
                                                render={({field}) => {
                                                    return (
                                                        <FormItem
                                                            key={race.id}
                                                            className="flex flex-row items-start space-x-3 space-y-0"
                                                        >
                                                            <FormControl>
                                                                <Checkbox
                                                                    checked={field.value?.includes(race.id)}
                                                                    onCheckedChange={(checked: boolean) => {
                                                                        return checked
                                                                            ? field.onChange([...(field.value ?? []), race.id])
                                                                            : field.onChange(
                                                                                field.value?.filter(
                                                                                    (value) => value !== race.id,
                                                                                ),
                                                                            );
                                                                    }}
                                                                />
                                                            </FormControl>
                                                            <FormLabel className="font-normal">
                                                                {race[getLocalizedFieldName("name") as keyof Criteria]}
                                                            </FormLabel>
                                                        </FormItem>
                                                    );
                                                }}
                                            />
                                        ))}
                                        <FormMessage/>
                                    </FormItem>
                                )}
                            />
                        )}

                        <FormField
                            control={form.control}
                            name="hasDisability"
                            render={({field}) => (
                                <FormItem className="space-y-3">
                                    <FormLabel className="text-base">8. {t("mentor.hasDisability")}</FormLabel>
                                    <YesNoRadioGroup field={field}/>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />

                        {form.watch('hasDisability') && (
                            <FormField
                                control={form.control}
                                name="disabilities"
                                render={() => (
                                    <FormItem>
                                        <div className="mb-4">
                                            <FormLabel className="text-base">8a. {t("criteria.disabilities")}</FormLabel>
                                            <FormDescription>

                                            </FormDescription>
                                        </div>
                                        {criteria?.Disability.map((disability) => (
                                            <FormField
                                                key={disability.id}
                                                control={form.control}
                                                name="disabilities"
                                                render={({field}) => {
                                                    return (
                                                        <FormItem
                                                            key={disability.id}
                                                            className="flex flex-row items-start space-x-3 space-y-0"
                                                        >
                                                            <FormControl>
                                                                <Checkbox
                                                                    checked={field.value?.includes(disability.id)}
                                                                    onCheckedChange={(checked: boolean) => {
                                                                        return checked
                                                                            ? field.onChange([...(field.value ?? []), disability.id])
                                                                            : field.onChange(
                                                                                field.value?.filter(
                                                                                    (value) => value !== disability.id,
                                                                                ),
                                                                            );
                                                                    }}
                                                                />
                                                            </FormControl>
                                                            <FormLabel className="font-normal">
                                                                {disability[getLocalizedFieldName("name") as keyof Criteria]}
                                                            </FormLabel>
                                                        </FormItem>
                                                    );
                                                }}
                                            />
                                        ))}
                                        <FormMessage/>
                                    </FormItem>
                                )}
                            />
                        )}

                        <FormField
                            control={form.control}
                            name="isIndigenous"
                            render={({field}) => (
                                <FormItem className="space-y-3">
                                    <FormLabel className="text-base">9. {t("mentor.isIndigenous")}</FormLabel>
                                    <FormControl>
                                        <RadioGroup
                                            onValueChange={value => field.onChange(value == 'true')}
                                            value={(field.value || field.value === false) ? field.value.toString() : undefined}
                                            defaultValue={(field.value || field.value === false) ? field.value.toString() : undefined}
                                            className="flex flex-col space-y-1"
                                        >
                                            <FormItem className="flex items-center space-x-3 space-y-0">
                                                <FormControl>
                                                    <RadioGroupItem value="true"/>
                                                </FormControl>
                                                <FormLabel className="font-normal">
                                                    {t("common.yes")}
                                                </FormLabel>
                                            </FormItem>
                                            <FormItem className="flex items-center space-x-3 space-y-0">
                                                <FormControl>
                                                    <RadioGroupItem value="false"/>
                                                </FormControl>
                                                <FormLabel className="font-normal">
                                                    {t("common.no")}
                                                </FormLabel>
                                            </FormItem>
                                        </RadioGroup>
                                    </FormControl>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />

                        {form.watch('isIndigenous') && (
                            <FormField
                                control={form.control}
                                name="indigeneities"
                                render={() => (
                                    <FormItem>
                                        <div className="mb-4">
                                            <FormLabel className="text-base">9a. {t("criteria.indigeneities")}</FormLabel>
                                            <FormDescription>
                                            </FormDescription>
                                        </div>
                                        {criteria?.Indigeneity.map((indigeneity) => (
                                            <FormField
                                                key={indigeneity.id}
                                                control={form.control}
                                                name="indigeneities"
                                                render={({field}) => {
                                                    return (
                                                        <FormItem
                                                            key={indigeneity.id}
                                                            className="flex flex-row items-start space-x-3 space-y-0"
                                                        >
                                                            <FormControl>
                                                                <Checkbox
                                                                    checked={field.value?.includes(indigeneity.id)}
                                                                    onCheckedChange={(checked: boolean) => {
                                                                        return checked
                                                                            ? field.onChange([...(field.value ?? []), indigeneity.id])
                                                                            : field.onChange(
                                                                                field.value?.filter(
                                                                                    (value) => value !== indigeneity.id,
                                                                                ),
                                                                            );
                                                                    }}
                                                                />
                                                            </FormControl>
                                                            <FormLabel className="font-normal">
                                                                {indigeneity[getLocalizedFieldName("name") as keyof Criteria]}
                                                            </FormLabel>
                                                        </FormItem>
                                                    );
                                                }}
                                            />
                                        ))}
                                        <FormMessage/>
                                    </FormItem>
                                )}
                            />
                        )}

                        <h3 className="text-xl">{t("mentor.genderSex")}</h3>
                        <p className="text-base text-text-2 !mt-3">
                            {t("mentor.genderSexDesc")}
                        </p>

                        <FormField
                            control={form.control}
                            name="isQueer"
                            render={({field}) => (
                                <FormItem className="space-y-3">
                                    <FormLabel className="text-base">10. {t("mentor.isQueer")}</FormLabel>
                                    <FormControl>
                                        <RadioGroup
                                            onValueChange={value => {
                                                field.onChange(value === "true");

                                                // If isQueer is changed to false, reset isMale and isFemale to opposite values
                                                if (value === "false") {
                                                    form.setValue('isMale', true);
                                                    form.trigger("isMale");
                                                    form.setValue('isFemale', false);
                                                    form.trigger("isFemale");
                                                }
                                            }}
                                            value={(field.value || field.value === false) ? field.value.toString() : undefined}
                                            defaultValue={(field.value || field.value === false) ? field.value.toString() : undefined}
                                            className="flex flex-col space-y-1"
                                        >
                                            <FormItem className="flex items-center space-x-3 space-y-0">
                                                <FormControl>
                                                    <RadioGroupItem value="true"/>
                                                </FormControl>
                                                <FormLabel className="font-normal">
                                                    {t("common.yes")}
                                                </FormLabel>
                                            </FormItem>
                                            <FormItem className="flex items-center space-x-3 space-y-0">
                                                <FormControl>
                                                    <RadioGroupItem value="false"/>
                                                </FormControl>
                                                <FormLabel className="font-normal">
                                                    {t("common.no")}
                                                </FormLabel>
                                            </FormItem>
                                        </RadioGroup>
                                    </FormControl>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />

                        {form.watch('isQueer') && (
                            <>
                                <FormField
                                    control={form.control}
                                    name="genderId"
                                    render={({field}) => (
                                        <FormItem>
                                            <FormLabel className="text-base">10a. {t("criteria.gender")}</FormLabel>
                                            <Select onValueChange={value => field.onChange(parseInt(value))}
                                                    value={field.value ? field.value.toString() : undefined}
                                            >
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder={t("mentor.selectGender")}/>
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    {criteria?.Gender.map((gender) => (
                                                        <SelectItem key={gender.id} value={gender.id.toString()}>
                                                            {gender[getLocalizedFieldName("name") as keyof Criteria]}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <FormDescription>

                                            </FormDescription>
                                            <FormMessage/>
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="sexualOrientationId"
                                    render={({field}) => (
                                        <FormItem>
                                            <FormLabel className="text-base">10b. {t("criteria.sexualOrientation")}</FormLabel>
                                            <Select onValueChange={value => field.onChange(parseInt(value))}
                                                    value={field.value ? field.value.toString() : undefined}
                                            >
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder={t("mentor.selectSexualOrientation")}/>
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    {criteria?.SexualOrientation.map((sexualOrientation) => (
                                                        <SelectItem key={sexualOrientation.id}
                                                                    value={sexualOrientation.id.toString()}>
                                                            {sexualOrientation[getLocalizedFieldName("name") as keyof Criteria]}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <FormDescription>

                                            </FormDescription>
                                            <FormMessage/>
                                        </FormItem>
                                    )}
                                />
                            </>
                        )}

                        <FormField
                            control={form.control}
                            name="isMale"
                            render={({field}) => (
                                <FormItem className="space-y-3">
                                    <FormLabel className="text-base">11. {t("mentor.isMale")}</FormLabel>
                                    <FormControl>
                                        <RadioGroup
                                            onValueChange={value => {
                                                field.onChange(value === "true");

                                                // If isQueer is false, flip the value of isFemale
                                                if (!form.watch('isQueer')) {
                                                    form.setValue('isFemale', value !== "true");
                                                    form.trigger("isFemale");
                                                }
                                            }}
                                            value={(field.value || field.value === false) ? field.value.toString() : undefined}
                                            defaultValue={(field.value || field.value === false) ? field.value.toString() : undefined}
                                            className="flex flex-col space-y-1"
                                        >
                                            <FormItem className="flex items-center space-x-3 space-y-0">
                                                <FormControl>
                                                    <RadioGroupItem value="true"/>
                                                </FormControl>
                                                <FormLabel className="font-normal">
                                                    {t("common.yes")}
                                                </FormLabel>
                                            </FormItem>
                                            <FormItem className="flex items-center space-x-3 space-y-0">
                                                <FormControl>
                                                    <RadioGroupItem value="false"/>
                                                </FormControl>
                                                <FormLabel className="font-normal">
                                                    {t("common.no")}
                                                </FormLabel>
                                            </FormItem>
                                        </RadioGroup>
                                    </FormControl>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="isFemale"
                            render={({field}) => (
                                <FormItem className="space-y-3">
                                    <FormLabel className="text-base">12. {t("mentor.isFemale")}</FormLabel>
                                    <FormControl>
                                        <RadioGroup
                                            onValueChange={value => {
                                                field.onChange(value === "true");

                                                // If isQueer is false, flip the value of isMale
                                                if (!form.watch('isQueer')) {
                                                    form.setValue('isMale', value !== "true");
                                                    form.trigger("isMale");
                                                }
                                            }}
                                            value={(field.value || field.value === false) ? field.value.toString() : undefined}
                                            defaultValue={(field.value || field.value === false) ? field.value.toString() : undefined}
                                            className="flex flex-col space-y-1"
                                        >
                                            <FormItem className="flex items-center space-x-3 space-y-0">
                                                <FormControl>
                                                    <RadioGroupItem value="true"/>
                                                </FormControl>
                                                <FormLabel className="font-normal">
                                                    {t("common.yes")}
                                                </FormLabel>
                                            </FormItem>
                                            <FormItem className="flex items-center space-x-3 space-y-0">
                                                <FormControl>
                                                    <RadioGroupItem value="false"/>
                                                </FormControl>
                                                <FormLabel className="font-normal">
                                                    {t("common.no")}
                                                </FormLabel>
                                            </FormItem>
                                        </RadioGroup>
                                    </FormControl>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="affinityGroupId"
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel className="text-base">13. {t("criteria.affinityGroup")}</FormLabel>
                                    <FormDescription className="text-text-2">
                                        {t("mentor.affinityGroupReadonlyDesc")}
                                    </FormDescription>
                                    <Select
                                        onValueChange={(value) => field.onChange(value === "none" ? null : parseInt(value))}
                                        value={field.value != null ? field.value.toString() : "none"}
                                        disabled={!isAdmin}
                                    >
                                        <FormControl>
                                            <SelectTrigger>
                                                <SelectValue placeholder={t("affinity.selectGroup")}/>
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="none">{t("common.none")}</SelectItem>
                                            {affinityGroups.map((group) => (
                                                <SelectItem key={group.id} value={group.id.toString()}>
                                                    {group[getLocalizedFieldName("name") as "name" | "nameFr"]}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />

                        <Button
                            type="submit"
                            disabled={!form.formState.isDirty || updateMutation.isPending}
                        >
                            {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin"/>}
                            {t("profile.updateProfile")}
                        </Button>
                        </form>
                    </Form>
                )}
            </CardContent>
        </Card>
    );
}

export default MentorProfileUpdateDetails;
